import React, { useEffect } from "react";
import { cn } from "@/lib/utils";
import { useOrder } from "../../../contexts/OrderContext";
import { useAudio } from "@/contexts/AudioContext";
import { Card } from "@/components/ui/card";
import { AlertCircleIcon, ArrowLeftIcon, ArrowRightIcon, ChevronsUpDownIcon, EarIcon, HistoryIcon, LoaderCircleIcon, MicIcon, SparkleIcon, SparklesIcon, SquareBottomDashedScissorsIcon, TextSelectIcon, TrashIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { OrderLoader, OrderPager } from "../Order";
import { Button } from "@/components/ui/button";
import { Navigate } from "react-router-dom"
import cournoyer from "@/cournoyer";
import { AutoResizingTextarea } from "../mandate/MandateForm";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import ReportPlayer from "./ReportPlayer";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useDocuments } from "@/contexts/DocumentsContext";
import { Separator } from "@/components/ui/separator";
import HtmlDiff from 'htmldiff-js';

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Guide from "@/Guide";

const { APP_API_URL } = import.meta.env

const transcriptionSections = cournoyer.filter(section => section.transcription_key || section.sections)

export default function Transcription({ readOnly }) {
    const { order, setOrder } = useOrder()
    const { setDocument, sectionsContainer } = useDocuments()
    const [loading, setLoading] = React.useState(false)
    const [historyOpen, setHistoryOpen] = React.useState(false)
    const navigate = useNavigate()
    const { session } = useAuth()
    const { playerNotification } = useAudio()
    const containerRef = React.useRef(null)

    const closeHistory = () => setHistoryOpen(false)
    const openHistory = (transcription_key) => setHistoryOpen(transcription_key)

    if (!order.mandate.reviewed || !order.supported) {
        return <Navigate to={`/orders/${order.pydio_id}/mandate`} />
    }

    React.useEffect(() => {
        if (!readOnly) {
            setDocument('report')
        }
    }, []);

    React.useEffect(() => {
        if (!order || !!order.transcription) return

        const intervalId = setInterval(async () => {
            try {
                const { data, error } = await supabase
                    .from('folders')
                    .select('correction')
                    .eq('pydio_id', order.pydio_id)
                    .single()

                console.log('Fetching transcription...', order.pydio_id)

                if (data?.correction.extracted_sections) {
                    setOrder((prevOrder) => ({
                        ...prevOrder,
                        transcription: data.correction?.extracted_sections,
                        transcription_not: data.correction?.not_extracted_sections_text?.trim().split("\n\n")
                    }))
                    playerNotification.current.play()
                    clearInterval(intervalId)
                }
            } catch (error) {
                console.error('Error fetching mandate:', error)
            }
        }, 5000)

        return () => clearInterval(intervalId)
    }, [order?.pydio_id])

    if (!order.supported) {
        return <div></div>
    }

    // Handle section navigation from URL query parameter
    const [searchParams, setSearchParams] = React.useState(new URLSearchParams(window.location.search));
    const sectionParam = searchParams.get('section');
    const contextParam = searchParams.get('context');

    React.useEffect(() => {
        scrollTo(sectionParam, false, true)
    }, [sectionParam]);

    React.useEffect(() => {
        scrollTo(contextParam)
    }, [contextParam]);

    const scrollTo = (key, transcription = true, report = false) => {
        if (transcription) {
            const targetElement = containerRef.current?.querySelector(`[data-transcription="${key}"]`);

            if (targetElement) {
                targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
                setTimeout(() => {
                    containerRef.current.scrollBy({ top: -10, behavior: "smooth" }); // Adjust -50px as needed
                }, 300);
            }
        }

        setTimeout(() => {
            const targetElementReport = sectionsContainer.current?.querySelector(`[data-${report ? 'report' : 'transcription'}="${key}"]`);

            if (targetElementReport) {
                targetElementReport.scrollIntoView({ behavior: "smooth", block: "start" });
                setTimeout(() => {
                    sectionsContainer.current.scrollBy({ top: -10, behavior: "smooth" }); // Adjust -50px as needed
                }, 300);
            }
        }, 500)
    }

    return <>
        <Card className="h-full w-full flex-grow min-h-0 overflow-auto">
            <Guide step="transcription" />
            {!order.transcription && <OrderLoader icon={TextSelectIcon} text="Analyse de la transcription" />}

            {!!order.transcription && <>
                <div className="flex flex-grow flex-col p-6 py-4 overflow-y-auto gap-6" ref={containerRef}>
                    {transcriptionSections.map((section, i) => {
                        return <>
                            <Section openHistory={openHistory} key={section.transcription_key} scrollTo={(param) => scrollTo(section.transcription_key, param)} section={section} readOnly={readOnly || !!order.finished_at || loading} />
                            {/* {i !== sections.length - 1 && <Separator />} */}
                        </>
                    })}
                </div>

                <Separator />

                <div className="flex flex-grow flex-col p-6 py-4 overflow-y-auto gap-6">
                    <TranscriptionNot />
                </div>
            </>}

            <HistoryModal close={closeHistory} open={historyOpen} section={historyOpen} />
        </Card>
    </>
}

export function TranscriptionNot() {
    const { order, setOrder } = useOrder()

    const transcription_not = order.transcription_not.filter(transcription => !!transcription.trim().length)

    return <Collapsible defaultOpen={true}>
        <CollapsibleTrigger className="flex items-center justify-between gap-1.5">
            <span className="font-semibold">Transcriptions non conservées ({transcription_not.length})</span>

            <Button variant="ghost" size="sm">
                <ChevronsUpDownIcon className="h-4 w-4" />
            </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
            {transcription_not.map((transcription, index) => (<AutoResizingTextarea key={index} className="flex-shrink-0 border-dashed" value={transcription.trim()} />))}
        </CollapsibleContent>
    </Collapsible>
}

function Section({ section, readOnly, scrollTo, openHistory }) {
    if (!section.transcription_key && !section.sections) {
        return <></>
    }

    const { order, setOrder, compareOrder } = useOrder()
    const { setTime } = useAudio()

    const update = (value) => {
        setOrder({
            ...order,
            is_transcription_dirty: true,
            transcription: {
                ...order.transcription,
                [section.transcription_key]: {
                    ...order.transcription[section.transcription_key],
                    text: value
                }
            }
        })
    }

    const value = section.transcription_key && order.transcription[section.transcription_key]
    const value_generated = section.transcription_key && order.transcription_generated[section.transcription_key]
    const hasValue = value?.text?.trim().length > 0

    const earSection = () => {
        setTime(order.audio.segments.segments[value.first_segment_id]?.start)
    }

    console.log('Transcription', compareOrder)

    return <div className="flex flex-col gap-2" data-transcription={section.transcription_key}>
        {section.transcription_key && <div className={cn("font-semibold select-none font-tt-norms")}>
            {section.labelTranscription || section.label}

            {hasValue && order.audio.segments && <Tooltip>
                <TooltipTrigger asChild>
                    <Button onClick={earSection} className="ml-1" style={{ position: 'relative', top: 1 }} size="icon" variant={"ghost"}>
                        <MicIcon />
                    </Button>
                </TooltipTrigger>

                <TooltipContent side="right">
                    <p>Écouter</p>
                </TooltipContent>
            </Tooltip>}

            {<Tooltip>
                <TooltipTrigger asChild>
                    <Button onClick={() => openHistory(section)} className="ml-1" style={{ position: 'relative', top: 1 }} size="icon" variant={"ghost"}>
                        <HistoryIcon />
                    </Button>
                </TooltipTrigger>

                <TooltipContent side="right">
                    <p>Historique</p>
                </TooltipContent>
            </Tooltip>}
        </div>}

        {!section.transcription_key && section.sections && <div className="flex flex-col gap-4">
            {section.sections.map((subsection, i) => <Section readOnly={readOnly} key={i} section={subsection} />)}
        </div>}

        {!!section.transcription_key && !!value && <>
            {!compareOrder && <AutoResizingTextarea readOnly={readOnly} value={value.text.trim()} onChange={(e) => update(e.target.value)} />}
            {compareOrder && <div dangerouslySetInnerHTML={{ __html: HtmlDiff.execute(value_generated.text.trim(), value.text.trim()) }} className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 border-dashed" />}

            {!readOnly && <div className="items-center gap-2 flex justify-between">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span></span>
                        {/* <Button disabled={!hasValue} size={"icon"} variant={"ghost"}><SquareBottomDashedScissorsIcon className="w-4 h-4" /></Button> */}
                    </TooltipTrigger>

                    <TooltipContent side="right">
                        <p>Déclarer un hors-sujet</p>
                    </TooltipContent>
                </Tooltip>
                <div className="flex items-center gap-2">
                    {!hasValue && !section.optionnal && <span className="text-sm text-orange-600 dark:text-orange-400">La section est obligatoire</span>}
                    <GenerateSectionButton scrollTo={scrollTo} transcriptionKey={section.transcription_key} disabled={!hasValue} />
                </div>
            </div>}
        </>}
    </div>
}

function GenerateSectionButton({ disabled, transcriptionKey, scrollTo }) {
    const { order, setOrder } = useOrder()
    const [loading, setLoading] = React.useState(false)
    const { session } = useAuth()

    const sections = React.useMemo(() => {
        if (transcriptionKey === 'antecedents') {
            return ['antecedents', 'medication', 'allergies', 'tobacco']
        } else if (['historyOfCurrentDisease', 'currentComplaints'].includes(transcriptionKey)) {
            return [transcriptionKey]
        } else {
            return [transcriptionSections.find(section => section.transcription_key === transcriptionKey)?.value]
        }
    }, [])

    const generate = async () => {
        setLoading(true)

        setOrder({
            ...order,
            sections: {
                ...order.sections,
                ...sections.reduce((a, b) => ({ ...a, [b]: 'LOADING' }), {})
            }
        })

        if (scrollTo) {
            scrollTo(false)
        }

        const { error, data } = await fetch(`${APP_API_URL}/generation/section/${order.pydio_id}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
                transcription: order.transcription,
                sections
            }),
        }).then(res => res.json())

        setLoading(false)

        if (!error) {
            setOrder({
                ...order,
                sections: {
                    ...order.sections,
                    ...data
                }
            })

            if (scrollTo) {
                scrollTo(true)
            }
        } else {
            alert(error.message)
            return false;
        }
    }

    const generated = sections.some(section => Object.keys(order.sections).includes(section))
    const generating = Object.keys(order.sections).some(section => order.sections[section] === "LOADING")

    return <Button variant={disabled ? "secondary" : generated ? "outline" : "default"} disabled={generating || disabled || loading} size={"sm"} onClick={generate}>
        {loading && <LoaderCircleIcon className="h-4 w-4 animate-spin" />}
        {!loading && <SparklesIcon className="mr-2 w-4 h-4" />}
        {loading ? null : generated ? "Regénérer" : "Génerer"}
    </Button>
}


function Transcription__() {
    const { order } = useOrder();
    const { seek, position } = useAudio();

    const [segment, setSegment] = React.useState(null);

    const containerRef = React.useRef(null);
    const [highlightRects, setHighlightRects] = React.useState([]);
    const highlightRef = React.useRef(null);

    React.useEffect(() => {
        if (seek > 0) {
            if (!segment) {
                return setSegment(order.transcription.segments[0])
            }

            if (seek > segment.end || seek < segment.start) {
                setSegment(order.transcription.segments.find(seg => seg.start <= seek && seg.end >= seek))
            }
        }

    }, [seek])

    React.useEffect(() => {
        const container = containerRef.current;

        if (container && segment) {
            const highlightSentence = segment.text.trim();
            const range = document.createRange();
            const nodes = Array.from(container.childNodes);
            const newRects = [];

            nodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE && node.textContent.includes(highlightSentence)) {
                    const startIndex = node.textContent.indexOf(highlightSentence);
                    const endIndex = startIndex + highlightSentence.length;

                    range.setStart(node, startIndex);
                    range.setEnd(node, endIndex);

                    const rects = Array.from(range.getClientRects());

                    rects.forEach((rect) => {
                        newRects.push({
                            top: rect.top - container.getBoundingClientRect().top,
                            left: rect.left - container.getBoundingClientRect().left,
                            width: rect.width,
                            height: rect.height,
                        });
                    });
                }
            });

            setHighlightRects(newRects);
        }
    }, [segment]);

    React.useEffect(() => {
        if (highlightRef.current) {
            const container = containerRef.current;
            const highlightRect = highlightRef.current.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            if (
                highlightRect.top < containerRect.top ||
                highlightRect.bottom > containerRect.bottom
            ) {
                highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    }, [highlightRects]);

    return (
        <div className="relative overflow-y-auto">
            {highlightRects.map((rect, index) => (
                <div
                    ref={index === 0 ? highlightRef : null}
                    key={index}
                    style={{
                        position: "absolute",
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height,
                        background: "rgb(180 190 255)",
                        transition: "all 0.3s ease-out",
                    }}
                    className="pointer-events-none absolute z-0 -ml-0.5 box-content rounded-md bg-primary p-0.5 transition-all duration-100 ease-out"
                ></div>
            ))}
            <div
                style={{ lineHeight: "1.8", fontSize: 14 }}
                className={cn("relative h-full p-6 whitespace-pre-wrap")}
                ref={containerRef}
            >
                {order.transcription.text.trim()}
            </div>
        </div>
    );
}

function HistoryModal({ section, open, close }) {
    const { order } = useOrder()

    const value = section.transcription_key && order.transcription_generated[section.transcription_key]

    return <Dialog open={open} onOpenChange={close}>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>{section.labelTranscription || section.label}</DialogTitle>
                <DialogDescription>
                    <AutoResizingTextarea className="flex-shrink-0 border-dashed" value={value?.text?.trim()} />
                </DialogDescription>
            </DialogHeader>
        </DialogContent>
    </Dialog>
}